import {
  CheckboxGroup,
  CheckboxGroupCreateArgs,
  CheckboxGroupResultData,
  CheckboxGroupTranslation,
  CheckboxGroupTranslationCreate,
  CheckboxGroupUpdateArgs,
  PagedApiResponse,
  QuestionResultsFilters,
} from "types";

import API from "api/client";
import { envConfig } from "config";
import { toObject } from "@civicscience/chops";

const { checkboxGroupsAPI } = envConfig;

type GetCheckboxGroupsArgs = {
  query: string;
  localeCode?: string | null;
  orderByTerm: string | null;
  orderTerm: string | null;
  page: number | string;
  size: number | string;
};

type GetCheckboxGroupResultsArgs = QuestionResultsFilters & {
  checkboxGroupId: string | number;
  isRebaseOptOut?: boolean;
};

/**
 * Creates a new CheckboxGroup with the provided args.
 */
const createCheckboxGroup = (req: CheckboxGroupCreateArgs): Promise<CheckboxGroup> => {
  return API.post(`${checkboxGroupsAPI}`, req).then((res) => res.data);
};

/**
 * Updates the CheckboxGroup with id `checkboxGroupId` with the provided args.
 */
const updateCheckboxGroup = (checkboxGroupId: number, req: CheckboxGroupUpdateArgs): Promise<CheckboxGroup> => {
  return API.put(`${checkboxGroupsAPI}${checkboxGroupId}`, req).then((res) => res.data);
};

/**
 * Gets the CheckboxGroup with id `checkboxGroupId`.
 */
const getCheckboxGroup = (checkboxGroupId: string | number): Promise<CheckboxGroup> => {
  return API.get(`${checkboxGroupsAPI}${checkboxGroupId}`).then((res) => res.data);
};

/**
 * Fetches the "result" data for a given CheckboxGroup.
 * This is the data that ultimately is provided to chart components.
 */
const getCheckboxGroupResults = ({
  checkboxGroupId,
  ...params
}: GetCheckboxGroupResultsArgs): Promise<CheckboxGroupResultData> => {
  return API.get(`${checkboxGroupsAPI}${checkboxGroupId}/c-results`, {
    params: {
      startDate: params.startDate,
      endDate: params.endDate,
      segment: params.segmentUUID || null,
      targetId: params.targetId,
      network: params.network,
      wscheme: params.weightingSchemeName,
      rebaseOptOut: params.isRebaseOptOut,
    },
  }).then((res) => res.data);
};

/**
 * Returns a list of `CheckboxGroup` instances based on the provided search args.
 */
const getCheckboxGroups = (options: GetCheckboxGroupsArgs): Promise<PagedApiResponse<CheckboxGroup[]>> => {
  const { query, localeCode, orderByTerm, orderTerm, page = 1, size = 10 } = toObject(options);
  return API.get(checkboxGroupsAPI, {
    params: { query, localeCode, orderBy: orderByTerm, order: orderTerm, page, size },
  }).then((res) => res.data);
};

/**
 * Creates a new translation for a checkbox group.
 * Note that this is really just creating a new CheckboxGroup with the locale set.
 */
const translateCheckboxGroup = (req: CheckboxGroupTranslationCreate): Promise<CheckboxGroup> => {
  return API.post(`${checkboxGroupsAPI}translations`, req).then((res) => res.data);
};

/**
 * Fetches the list of translations for the CheckboxGroup with id `checkboxGroupId`.
 */
const getCheckboxGroupTranslations = (checkboxGroupId: string | number): Promise<CheckboxGroupTranslation[]> => {
  return API.get(`${checkboxGroupsAPI}${checkboxGroupId}/translations`).then((res) => res.data);
};

/**
 * Favorite a checkbox group.
 */
const favoriteCheckboxGroup = (checkboxGroupId: string | number) => {
  return API.post(`${checkboxGroupsAPI}${checkboxGroupId}/favorite`).then((res) => res.data);
};

/**
 *
 * Unfavorite a checkbox group.
 */
const unfavoriteCheckboxGroup = (checkboxGroupId: string | number) => {
  return API.delete(`${checkboxGroupsAPI}${checkboxGroupId}/favorite`).then((res) => res.data);
};

/**
 *
 * Delete a checkbox group with soft remove.
 */
const deleteCheckboxGroup = (checkboxGroupId: string | number) => {
  return API.delete(`${checkboxGroupsAPI}${checkboxGroupId}`).then((res) => res.data);
};

export default {
  create: createCheckboxGroup,
  favorite: favoriteCheckboxGroup,
  get: getCheckboxGroup,
  getResults: getCheckboxGroupResults,
  update: updateCheckboxGroup,
  list: getCheckboxGroups,
  translate: translateCheckboxGroup,
  listTranslations: getCheckboxGroupTranslations,
  unfavorite: unfavoriteCheckboxGroup,
  delete: deleteCheckboxGroup,
};
